/*
 * WHAT: A unified "Certification Workspace" that takes privileges OR predefined
 *       roles from UNASSIGNED -> leveled -> certified in one screen, and also
 *       lets a DBA walk a classification back (unassign / uncertify), inspect
 *       its full audit history, and view all levels "as on a date".
 * WHY:  Consolidates the old multi-screen flow and satisfies the MSSQL
 *       requirements:
 *         1. unassign + uncertify, individually and in bulk
 *         2. a documentation suggestion is shown for EVERY row (the backend
 *            now computes suggestions for assigned/certified rows too)
 *         3. the real signed-in user is recorded + shown as certified-by
 *         4. certified/uncertified dates + a per-row change-history timeline
 *         6. an as-of-date viewer that replays the audit log
 *
 *       It is parameterized by `target` so the same component serves both the
 *       Privileges tab (target='privilege') and the Roles tab (target='role').
 *
 * Endpoints:
 *   privilege: privilege-reference/privileges|update-level|update-certification|
 *              bulk-update-certification|bulk-apply-suggested|change-state
 *   role:      privilege-reference/roles|update-role-level|
 *              update-role-certification|bulk-apply-suggested(target=role)|
 *              change-state(target=role)
 *   audit:     privilege-reference/audit/history | privilege-reference/audit/as-of
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import WideModal from './WideModal';

const LEVEL_COLORS = {
  UNASSIGNED: { color: '#dc3545', bg: '#f8d7da' },
  NONE: { color: '#6c757d', bg: '#e9ecef' },
  READ: { color: '#0d6efd', bg: '#cfe2ff' },
  READ_ELEVATED: { color: '#0dcaf0', bg: '#cff4fc' },
  WRITE: { color: '#fd7e14', bg: '#ffe5d0' },
  WRITE_ELEVATED: { color: '#d63384', bg: '#f7d6e6' },
  ADMIN: { color: '#dc3545', bg: '#f8d7da' },
  SUPER_ADMIN: { color: '#6f42c1', bg: '#e2d9f3' },
};

function LevelBadge({ name }) {
  const c = LEVEL_COLORS[name] || { color: '#6c757d', bg: '#e9ecef' };
  return (
    <span style={{
      background: c.bg, color: c.color, padding: '2px 8px',
      borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>{name || '—'}</span>
  );
}

// Workflow state of a single row, derived from its level + certified flag.
function rowState(row) {
  if (row.privilege_level_name === 'UNASSIGNED' || row.privilege_level < 0) return 'unassigned';
  if (!row.certified) return 'uncertified';
  return 'certified';
}

// Is this ROLE row a "maintained"/built-in role (vs a user-defined role)?
// Used for the maintained/user-defined KIND filter + label, NOT for read-only
// (see isCertifiableRole). Generalizes across techs: Oracle predefined roles
// carry is_oracle_maintained; MSSQL fixed-server/database, MS-shipped roles,
// AND `public` carry is_fixed_role / is_mssql_maintained.
function isMaintainedRole(row) {
  return !!(row.is_oracle_maintained || row.is_fixed_role || row.is_mssql_maintained);
}

// Is this role independently CERTIFIABLE here (assign + suggest + certify),
// rather than read-only/derived? Only TRUE fixed roles are: their level is a
// global fixed fact (db_owner = ADMIN everywhere). Everything else derives its
// level from its component privileges per-database and is shown read-only:
//   * MSSQL user-defined roles, AND
//   * MSSQL `public` — built-in (is_mssql_maintained) but per-db, level derived
//     from its grants, so NOT a fixed global fact (is_fixed_role is false).
// Oracle roles use the component-level `derived` prop instead (all read-only).
function isCertifiableRole(row) {
  return !!row.is_fixed_role;
}

// Read-only role row = a role that is not independently certifiable. In derived
// mode (Oracle) the whole component is read-only, so this is only consulted in
// non-derived (MSSQL) mode.
function isReadOnlyRoleRow(row, isRole) {
  return isRole && !isCertifiableRole(row);
}

function fmtDate(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
}

export default function CertificationWorkspace({
  databaseType,
  target,            // 'privilege' | 'role'
  privilegeLevels,   // [{value,name}] real assignable tiers (matrix-aware)
  currentUser,
  onDataChanged,     // called after any successful mutation so the parent
                     // can refresh the certification banner/stats
  derived = false,   // when true the rows are READ-ONLY, computed-from-
                     // privileges (Oracle roles): no suggestion, no assign,
                     // no certify/uncertify/unassign, no bulk bar. The level
                     // is derived from the row's component privileges and a
                     // row is UNASSIGNED until every component is classified.
}) {
  const isRole = target === 'role';
  // Oracle roles expose is_oracle_maintained — a derived role view offers a
  // maintained vs user-defined filter (global predefined roles vs per-db
  // user-defined). 'all' | 'maintained' | 'user'.
  const [roleKind, setRoleKind] = useState('all');
  const ep = (p) => API_CONFIG.getDatabasesEndpoint(p);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // show everything by default; chips are quick filters
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  // §K63: a PERSISTENT, dismissable note (distinct from the transient toast) for
  // important caveats the DBA must actually read — e.g. the one-hop cascade note
  // after a role-level change. Stays until dismissed; not auto-cleared.
  const [note, setNote] = useState(null);
  // Dropdown filters (narrow the list AND scope the bulk actions).
  const [fSuggested, setFSuggested] = useState('');   // suggested_level_name
  const [fCurrent, setFCurrent] = useState('');       // privilege_level_name
  const [fType, setFType] = useState('');             // privilege_type (privileges only)
  // Review modal before bulk-accepting suggestions.
  const [reviewModal, setReviewModal] = useState(null); // { rows:[], certify:bool, included:Set }
  // Review modal before bulk unassign / uncertify (reverse actions).
  const [stateModal, setStateModal] = useState(null); // { action, rows:[], included:Set }
  // Confirmation modal before a SINGLE-ROW unassign / uncertify. Single-row
  // reverse actions used to fire immediately on one click; this modal makes
  // the DBA confirm with full detail (what changes, downstream recompute).
  const [confirmModal, setConfirmModal] = useState(null); // { action, row }
  // Global audit-report modal.
  const [auditModal, setAuditModal] = useState(null); // { loading, entries:[], total, filters }
  // Per-row change-history modal.
  const [historyModal, setHistoryModal] = useState(null); // { name, loading, rows:[] }
  // Oracle derived roles: per-component privilege-level breakdown modal,
  // opened by clicking a role's Derived Level. { name, loading, data, error }
  const [componentModal, setComponentModal] = useState(null);
  // As-of-date viewer modal.
  const [asOfModal, setAsOfModal] = useState(null); // { date, loading, items:[] }
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = isRole
        ? 'privilege-reference/roles/'
        : 'privilege-reference/privileges/';
      const resp = await axios.get(ep(path), {
        params: { database_type: databaseType },
      });
      const data = isRole ? (resp.data.roles || []) : (resp.data.privileges || []);
      // Show EVERY role the backend returns (the backend already scopes to
      // active databases). True FIXED roles are certifiable; everything else
      // (user-defined roles AND built-in-but-per-db `public`) is shown
      // READ-ONLY — level inherited from component privileges, not assigned or
      // certified here — see isCertifiableRole()/isReadOnlyRoleRow(). Mirrors
      // the Oracle derived-role treatment while keeping fixed roles certifiable.
      setRows(data);
      setSelected(new Set());
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [databaseType, isRole]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // A row is read-only/derived (non-certifiable) when, in non-derived mode, it
  // is a role that is not independently certifiable (MSSQL `public` + any
  // user-defined role). Their level is inherited, not assigned/certified here.
  const isDerivedRow = useCallback(
    (r) => isRole && !derived && !isCertifiableRole(r), [isRole, derived]);

  const counts = useMemo(() => {
    // Status chips count by workflow state. CERTIFIABLE rows split across
    // unassigned/uncertified/certified; READ-ONLY/derived rows are counted
    // separately as `derived` (they have no certify workflow). All four buckets
    // sum to `total`, so the row reconciles with the Role-kind row below.
    const c = { unassigned: 0, uncertified: 0, certified: 0, derived: 0, total: rows.length };
    rows.forEach(r => {
      if (isDerivedRow(r)) c.derived++;
      else c[rowState(r)]++;
    });
    return c;
  }, [rows, isDerivedRow]);

  // Role-kind counts. Buckets that sum to total:
  //   * maintained — built-in roles. In MSSQL (non-derived) this is the
  //     CERTIFIABLE subset (true fixed roles only); the built-in-but-read-only
  //     `public` is split into its own `derived` bucket so Fixed/built-in stays
  //     purely certifiable. In Oracle (derived) ALL roles are read-only, so the
  //     split would empty `maintained` — there we keep the simple
  //     maintained-vs-user split (no separate derived bucket).
  //   * derived    — built-in but read-only/per-db (MSSQL `public`). 0 in Oracle.
  //   * user       — user-defined roles.
  const roleKindCounts = useMemo(() => {
    if (!isRole) return { all: 0, maintained: 0, derived: 0, user: 0 };
    let maintained = 0, derivedKind = 0, user = 0;
    rows.forEach(r => {
      if (!isMaintainedRole(r)) { user++; return; }
      if (!derived && !isCertifiableRole(r)) derivedKind++;  // built-in but not fixed (public)
      else maintained++;
    });
    return { all: rows.length, maintained, derived: derivedKind, user };
  }, [rows, isRole, derived]);

  // Derived roles (Oracle): distribution by derived level. Roles are not
  // independently certified — the meaningful summary is "how many roles fell
  // at each derived tier". Used to render the clickable level-distribution
  // cards that replace the certified/uncertified/unassigned status chips.
  const levelCounts = useMemo(() => {
    const c = {};
    rows.forEach(r => {
      const k = r.privilege_level < 0 || !r.privilege_level_name
        ? 'UNASSIGNED'
        : r.privilege_level_name;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [rows]);

  // Distinct privilege types for the Type filter (privileges only).
  const typeOptions = useMemo(() => {
    if (isRole) return [];
    return [...new Set(rows.map(r => r.privilege_type).filter(Boolean))].sort();
  }, [rows, isRole]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(r => {
      const ro = !derived && isReadOnlyRoleRow(r, isRole); // read-only/derived row
      // Status chips: the 'derived' chip shows ONLY read-only rows; the
      // certify-workflow chips (unassigned/uncertified/certified) show only
      // CERTIFIABLE rows (read-only rows have no such state). 'all' shows both.
      if (statusFilter === 'derived') {
        if (!ro) return false;
      } else if (statusFilter !== 'all') {
        if (ro || rowState(r) !== statusFilter) return false;
      }
      // Read-only rows have no suggestion, so they're hidden under that filter.
      if (fSuggested && (ro || (r.suggested_level_name || '') !== fSuggested)) return false;
      if (fCurrent && (r.privilege_level_name || '') !== fCurrent) return false;
      if (fType && !isRole && (r.privilege_type || '') !== fType) return false;
      // Role-kind filter: maintained = certifiable built-in; derived = built-in
      // read-only (public); user = user-defined.
      if (isRole && roleKind === 'maintained' && !(isMaintainedRole(r) && isCertifiableRole(r))) return false;
      if (isRole && roleKind === 'derived' && !(isMaintainedRole(r) && !isCertifiableRole(r))) return false;
      if (isRole && roleKind === 'user' && isMaintainedRole(r)) return false;
      if (term) {
        const nm = (isRole ? r.role_name : r.privilege_name) || '';
        if (!nm.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, search, isRole, derived, fSuggested, fCurrent, fType, roleKind]);

  // Row identity for React keys + selection sets. A bare r.id is NOT unique
  // here: the MSSQL roles endpoint UNIONs two tables (role categorization +
  // FIXED_ROLE reference) whose id sequences overlap, and the MySQL/Cassandra
  // role payloads carry no id at all — duplicate/undefined keys make React
  // reuse row DOM across filter switches, rendering rows under the wrong
  // filter and duplicating them. Compose the row's natural identity instead.
  const rowKey = (r) => [
    r.id ?? '',
    r.role_name ?? r.privilege_name ?? '',
    r.database_name ?? '',
    r.is_server_role ?? '',
    r.is_fixed_role ?? '',
  ].join('|');
  const rowName = (r) => (isRole ? r.role_name : r.privilege_name);
  // Display variant: per-database roles (user-defined roles in Oracle AND
  // MSSQL, plus MSSQL's built-in-but-per-db `public`) carry a database_name and
  // may repeat the same name across DBs. Append the DB in parentheses so the
  // rows are distinguishable. Truly-global roles (Oracle-maintained, MSSQL
  // fixed roles) have database_name = NULL from the API, so they stay bare.
  // Keep rowName() unsuffixed — it is also the API lookup key and toast text.
  const rowDisplayName = (r) => {
    const nm = rowName(r);
    if (isRole && r.database_name) {
      return `${nm} (${r.database_name})`;
    }
    return nm;
  };

  const flash = (msg, ok = true, ms = 4000) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), ms);
  };

  const afterMutation = async (msg, ok = true) => {
    if (msg) flash(msg, ok);
    await fetchRows();
    onDataChanged && onDataChanged();
  };

  // ---- single-row actions -------------------------------------------------
  const assignLevel = async (row, levelName) => {
    const lvl = privilegeLevels.find(l => l.name === levelName);
    if (!lvl) return;
    setBusy(true);
    try {
      const path = isRole
        ? 'privilege-reference/update-role-level/'
        : 'privilege-reference/update-level/';
      // MSSQL fixed roles live in mssql_privilege_mapping_reference (global),
      // NOT in mssql_role_categorization. Without role_scope='FIXED_ROLE' the
      // backend hits the per-db role table and returns 404.
      const isMssqlFixed = isRole && databaseType === 'MSSQL' &&
        (row.is_fixed_role || row.role_scope === 'FIXED_ROLE');
      const body = isRole
        ? { database_type: databaseType, role_id: row.id,
            privilege_level: lvl.value, privilege_level_name: lvl.name,
            ...(isMssqlFixed ? { role_scope: 'FIXED_ROLE' } : {}) }
        : { database_type: databaseType, privilege_id: row.id,
            privilege_level: lvl.value, privilege_level_name: lvl.name };
      const resp = await axios.post(ep(path), body);
      // §K63: the one-hop cascade note (role-level changes only) goes into the
      // PERSISTENT note banner so it stays until the DBA dismisses it — a
      // compliance caveat, not a fleeting toast. The action itself still gets a
      // normal short success toast.
      const cascadeNote = resp.data?.cascade_note;
      if (cascadeNote) setNote(cascadeNote);
      await afterMutation(`Assigned ${rowName(row)} → ${lvl.name}`);
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  const certifyRow = async (row) => {
    setBusy(true);
    try {
      const path = isRole
        ? 'privilege-reference/update-role-certification/'
        : 'privilege-reference/update-certification/';
      const isMssqlFixed = isRole && databaseType === 'MSSQL' &&
        (row.is_fixed_role || row.role_scope === 'FIXED_ROLE');
      const body = isRole
        ? { database_type: databaseType, role_ids: [row.id],
            certified: true, certified_by: currentUser,
            ...(isMssqlFixed ? { role_scope: 'FIXED_ROLE' } : {}) }
        : { database_type: databaseType, privilege_id: row.id,
            certified: true, certified_by: currentUser };
      await axios.post(ep(path), body);
      await afterMutation(`Certified ${rowName(row)}`);
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  // True iff the row is a MSSQL fixed role — its id references the GLOBAL
  // mssql_privilege_mapping_reference (Task #210 removed the per-db copies),
  // so any mutation endpoint MUST receive role_scope='FIXED_ROLE' to look the
  // id up in the right table.
  const isMssqlFixedRow = (r) =>
    isRole && databaseType === 'MSSQL' &&
    (r.is_fixed_role || r.role_scope === 'FIXED_ROLE');

  // Reverse-direction single-row actions (req 1). Both go through the
  // unified change-state endpoint, which audits old->new (req 4).
  const changeState = async (action, ids, label, roleScope) => {
    if (!ids.length) { flash(`No ${action === 'unassign' ? 'assignable' : 'certified'} rows in scope.`, false); return; }
    setBusy(true);
    try {
      const resp = await axios.post(ep('privilege-reference/change-state/'), {
        database_type: databaseType,
        target,
        action,
        ids,
        changed_by: currentUser,
        source: ids.length > 1 ? 'workspace bulk' : 'workspace single',
        ...(roleScope ? { role_scope: roleScope } : {}),
      });
      await afterMutation(resp.data.message || label);
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  // Open the single-row confirmation modal (never act on one click).
  const openConfirm = (action, row) => setConfirmModal({ action, row });

  // Apply the confirmed single-row reverse action via the same audited
  // change-state endpoint.
  const confirmSingle = async () => {
    const m = confirmModal;
    if (!m) return;
    setConfirmModal(null);
    await changeState(
      m.action, [m.row.id],
      m.action === 'unassign' ? 'Unassigned.' : 'Uncertified.',
      isMssqlFixedRow(m.row) ? 'FIXED_ROLE' : null);
  };

  // A row is read-only when it's a user-defined role in non-derived mode
  // (MSSQL): its level is inherited from its component privileges and cannot be
  // assigned/suggested/certified here. (In derived mode the whole component is
  // read-only, so this is moot.)
  const isReadOnlyRow = useCallback(
    (r) => !derived && isReadOnlyRoleRow(r, isRole), [derived, isRole]);

  // ---- bulk actions -------------------------------------------------------
  // Rows the bulk actions / select-all may touch: visible minus read-only.
  const selectableVisible = useMemo(
    () => visible.filter(r => !isReadOnlyRow(r)), [visible, isReadOnlyRow]);

  const selectedRows = useMemo(
    () => visible.filter(r => selected.has(rowKey(r)) && !isReadOnlyRow(r)),
    [visible, selected, isReadOnlyRow]);

  // Scope = explicit selection if any, else every selectable visible row
  // (never the read-only user-defined rows).
  const scopeRows = () => (selectedRows.length ? selectedRows : selectableVisible);

  // Open a review modal first (NEVER fire bulk reverse actions on one click).
  // Scope = selection if any, else the filtered list; the DBA then checks
  // exactly which rows to unassign/uncertify before applying.
  const openStateReview = (action) => {
    const scope = scopeRows().filter(r =>
      action === 'unassign'
        ? rowState(r) !== 'unassigned'        // anything assigned/certified
        : rowState(r) === 'certified');        // only certified can uncertify
    if (!scope.length) {
      flash(action === 'unassign'
        ? 'No assigned rows in scope to unassign.'
        : 'No certified rows in scope to uncertify.', false);
      return;
    }
    setStateModal({ action, rows: scope, included: new Set(scope.map(rowKey)) });
  };

  const confirmStateReview = async () => {
    const m = stateModal;
    if (!m) return;
    const included = m.rows.filter(r => m.included.has(rowKey(r)));
    const ids = included.map(r => r.id);
    if (!ids.length) { flash('No rows included.', false); return; }
    // MSSQL fixed roles target the GLOBAL FIXED_ROLE reference, not the per-db
    // mssql_role_categorization table. If EVERY included row is a fixed role,
    // signal that to the backend so it routes to the global ref + helpers.
    // Mixed batches (fixed + user-defined) shouldn't happen in practice
    // (user-defined are derived and not eligible for restate), but if one
    // sneaks in we omit the scope and let the backend's safety-net fallback
    // pick up the fixed ids.
    const allFixed = included.length > 0 && included.every(isMssqlFixedRow);
    setBusy(true);
    try {
      const resp = await axios.post(ep('privilege-reference/change-state/'), {
        database_type: databaseType, target, action: m.action, ids,
        changed_by: currentUser, source: 'workspace bulk',
        ...(allFixed ? { role_scope: 'FIXED_ROLE' } : {}),
      });
      setStateModal(null);
      await afterMutation(resp.data.message
        || (m.action === 'unassign' ? 'Unassigned.' : 'Uncertified.'));
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  // Open the review modal for the in-scope UNASSIGNED-with-suggestion rows.
  const openReview = (certify) => {
    const scope = scopeRows()
      .filter(r => rowState(r) === 'unassigned' && r.suggested_level_name);
    if (!scope.length) { flash('No UNASSIGNED rows with a suggestion in scope.', false); return; }
    setReviewModal({
      rows: scope,
      certify,
      included: new Set(scope.map(rowKey)),
    });
  };

  const confirmReview = async () => {
    const m = reviewModal;
    if (!m) return;
    const ids = m.rows.filter(r => m.included.has(rowKey(r))).map(r => r.id);
    if (!ids.length) { flash('No rows included.', false); return; }
    setBusy(true);
    try {
      const resp = await axios.post(ep('privilege-reference/bulk-apply-suggested/'), {
        database_type: databaseType,
        target,
        privilege_ids: ids,
        certify: m.certify,
        certified_by: m.certify ? currentUser : null,
      });
      setReviewModal(null);
      await afterMutation(resp.data.message || (m.certify
        ? 'Suggestions applied & certified.' : 'Suggestions applied.'));
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  const certifyAllAssigned = async () => {
    const scope = scopeRows().filter(r => rowState(r) === 'uncertified');
    if (!scope.length) { flash('No assigned-but-uncertified rows in scope.', false); return; }
    setBusy(true);
    try {
      if (isRole) {
        await axios.post(ep('privilege-reference/update-role-certification/'), {
          database_type: databaseType, role_ids: scope.map(r => r.id),
          certified: true, certified_by: currentUser,
        });
      } else {
        await axios.post(ep('privilege-reference/bulk-update-certification/'), {
          database_type: databaseType, privilege_ids: scope.map(r => r.id),
          certified: true, certified_by: currentUser,
        });
      }
      await afterMutation(`Certified ${scope.length} item(s).`);
    } catch (e) {
      flash(e.response?.data?.message || e.message, false);
    } finally { setBusy(false); }
  };

  // ---- audit views (req 4 & 6) -------------------------------------------
  const openHistory = async (row) => {
    const name = rowName(row);
    // §K49 (2026-08-17): pass the row's SCOPE so the timeline is this row's
    // alone — the same privilege name exists in several scopes on every tech
    // (MySQL ALTER GLOBAL/TABLE/SCHEMA, Cassandra SELECT ×6 …) and a name-only
    // lookup mixed all of them into one modal. MSSQL fixed roles are audited
    // under scope 'FIXED_ROLE'; other roles carry no scope.
    const scope = isRole
      ? ((databaseType === 'MSSQL' && (row.is_fixed_role || row.role_scope === 'FIXED_ROLE'))
          ? 'FIXED_ROLE' : undefined)
      : (row.privilege_type || row.privilege_scope || row.permission_scope || undefined);
    setHistoryModal({ name, loading: true, rows: [] });
    try {
      const resp = await axios.get(ep('privilege-reference/audit/history/'), {
        params: { database_type: databaseType, target, name, ...(scope ? { scope } : {}) },
      });
      setHistoryModal({ name, loading: false, rows: resp.data.history || [] });
    } catch (e) {
      setHistoryModal({ name, loading: false, rows: [],
        error: e.response?.data?.message || e.message });
    }
  };

  // Oracle derived roles: fetch the per-component privilege-level breakdown
  // behind a role's derived level. Shows WHY the role is at that level — which
  // component privilege(s) drive the MAX, and which (if any) are UNASSIGNED and
  // therefore tainting the role.
  const openComponents = async (row) => {
    const name = rowName(row);
    setComponentModal({ name, loading: true, data: null });
    try {
      // MSSQL derived roles (user-defined + public) key off the exact
      // categorization row id (unambiguous across per-DB / server-vs-database
      // scoping); Oracle keys off role_name + dbid; MySQL keys off role_name +
      // optional source_db_id. All three endpoints return the same
      // component-breakdown shape so the modal below renders any of them.
      let resp;
      if (databaseType === 'MSSQL') {
        resp = await axios.get(ep('privilege-reference/mssql-role-component-levels/'), {
          params: { database_type: 'MSSQL', role_id: row.id },
        });
      } else if (databaseType === 'MYSQL') {
        resp = await axios.get(ep('privilege-reference/mysql-role-component-levels/'), {
          params: {
            database_type: 'MYSQL',
            role_name: name,
            source_db_id: row.source_db_id || row.dbid || undefined,
          },
        });
      } else if (databaseType === 'CASSANDRA') {
        // Every Cassandra role is derived; the breakdown reads the same
        // resolved_permissions the categorizer stored, then applies the
        // (permission, scope) reference lookup + matrix clamp.
        resp = await axios.get(ep('privilege-reference/cassandra-role-component-levels/'), {
          params: {
            database_type: 'CASSANDRA',
            role_name: name,
            source_db_id: row.source_db_id || undefined,
          },
        });
      } else {
        // Oracle (and any future tech that uses the role_name + dbid endpoint).
        resp = await axios.get(ep('privilege-reference/role-component-levels/'), {
          params: { database_type: databaseType, role_name: name, dbid: row.dbid || undefined },
        });
      }
      setComponentModal({ name, loading: false, data: resp.data });
    } catch (e) {
      setComponentModal({ name, loading: false, data: null,
        error: e.response?.data?.message || e.message });
    }
  };

  const openAsOf = async () => {
    setAsOfModal({ date: asOfDate, loading: true, items: [] });
    try {
      const resp = await axios.get(ep('privilege-reference/audit/as-of/'), {
        params: { database_type: databaseType, target, as_of: asOfDate },
      });
      setAsOfModal({ date: asOfDate, loading: false, items: resp.data.items || [] });
    } catch (e) {
      setAsOfModal({ date: asOfDate, loading: false, items: [],
        error: e.response?.data?.message || e.message });
    }
  };

  // Global audit report — every recorded action for this technology/target.
  const loadAudit = async (filters) => {
    setAuditModal(m => ({ ...(m || {}), loading: true,
      entries: m?.entries || [], total: m?.total || 0, filters }));
    try {
      const resp = await axios.get(ep('privilege-reference/audit/report/'), {
        params: {
          database_type: databaseType,
          target,
          action: filters.action || undefined,
          changed_by: filters.changedBy || undefined,
          search: filters.search || undefined,
          since: filters.since || undefined,
          until: filters.until || undefined,
          limit: 1000,
        },
      });
      setAuditModal({
        loading: false,
        entries: resp.data.entries || [],
        total: resp.data.total || 0,
        filters,
      });
    } catch (e) {
      setAuditModal({ loading: false, entries: [], total: 0, filters,
        error: e.response?.data?.message || e.message });
    }
  };
  const openAudit = () => {
    const filters = { action: '', changedBy: '', search: '', since: '', until: '' };
    loadAudit(filters);
  };

  const toggleSel = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleSelAll = (on) =>
    setSelected(on ? new Set(selectableVisible.map(rowKey)) : new Set());

  // ---- render -------------------------------------------------------------
  const chip = (key, label, color) => {
    const active = statusFilter === key;
    const value = key === 'all' ? counts.total : (counts[key] ?? 0);
    return (
      <button
        key={key}
        onClick={() => { setStatusFilter(key); setSelected(new Set()); }}
        title={active ? `Showing ${label}` : `Click to filter: ${label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px',
          border: active ? `1.5px solid ${color}` : '1px solid #dee2e6',
          background: active ? color + '1a' : '#fff', borderRadius: '999px',
          cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.8rem',
        }}
      >
        <span style={{ fontWeight: 700, color, fontSize: '0.95rem' }}>{value}</span>
        <span style={{ color: active ? color : '#555' }}>{label}</span>
      </button>
    );
  };

  // Card-style toggle for the Oracle derived-role kind filter (maintained /
  // user-defined). Same visual language as the status chips above.
  const kindChip = (key, label, color) => {
    const active = roleKind === key;
    const value = roleKindCounts[key] ?? 0;
    return (
      <button
        key={key}
        onClick={() => { setRoleKind(key); setSelected(new Set()); }}
        title={active ? `Showing ${label}` : `Click to filter: ${label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px',
          border: active ? `1.5px solid ${color}` : '1px solid #dee2e6',
          background: active ? color + '1a' : '#fff', borderRadius: '999px',
          cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.8rem',
        }}
      >
        <span style={{ fontWeight: 700, color, fontSize: '0.95rem' }}>{value}</span>
        <span style={{ color: active ? color : '#555' }}>{label}</span>
      </button>
    );
  };

  // Color per derived level (severity desc). UNASSIGNED reuses the red
  // sentinel color used by the privilege status chips.
  const levelColor = (name) => ({
    UNASSIGNED:     '#dc3545',
    SUPER_ADMIN:    '#7c1d6f',
    ADMIN:          '#b91c1c',
    WRITE_ELEVATED: '#c2410c',
    WRITE:          '#d97706',
    READ_ELEVATED:  '#0369a1',
    READ:           '#0d6efd',
    NONE:           '#6c757d',
  }[name] || '#6c757d');

  // Card-style toggle for the derived-level distribution (Oracle Roles tab).
  // Clicking filters the visible roles to that level via the existing
  // current-level filter (fCurrent), so the level dropdown and these cards
  // stay in sync.
  const levelChip = (levelName, label) => {
    const value = levelCounts[levelName] || 0;
    const active = fCurrent === levelName;
    const color = levelColor(levelName);
    return (
      <button
        key={levelName}
        onClick={() => { setFCurrent(active ? '' : levelName); setSelected(new Set()); }}
        title={active ? `Showing ${label}` : `Click to filter: ${label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px',
          border: active ? `1.5px solid ${color}` : '1px solid #dee2e6',
          background: active ? color + '1a' : '#fff', borderRadius: '999px',
          cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.8rem',
        }}
      >
        <span style={{ fontWeight: 700, color, fontSize: '0.95rem' }}>{value}</span>
        <span style={{ color: active ? color : '#555' }}>{label}</span>
      </button>
    );
  };

  // The certified ratio covers only CERTIFIABLE rows (total minus derived
  // read-only rows), so "X/Y certified" isn't diluted by `public`/derived roles
  // that have no certify workflow.
  const certifiableTotal = counts.total - counts.derived;
  const pct = certifiableTotal ? Math.round((counts.certified / certifiableTotal) * 100) : 0;
  const noun = isRole ? 'role' : 'privilege';
  // The maintained/user-defined kind filter applies to Oracle (derived) roles
  // AND MSSQL roles (where fixed roles are global/certifiable and user-defined
  // roles are per-db/read-only). The "maintained" label differs per tech.
  const showKindFilter = isRole && (derived || databaseType === 'MSSQL');
  const maintainedLabel = databaseType === 'ORACLE' ? 'Oracle-maintained' : 'Fixed / built-in';

  return (
    <div>
      {/* Compact header: title + certified ratio + slim filter pills, all on
          one wrap-friendly line. The pills double as status filters (their
          count is shown inline, so the old progress bar + hint line are
          redundant and were removed to reclaim vertical space).

          DERIVED ROLES (Oracle): roles are NOT independently certified — the
          level is computed from their component privileges. So the
          unassigned/not-certified/certified status chips are replaced with a
          distribution of derived levels (clicking a level card filters the
          list to that tier, via the existing fCurrent filter). */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: '8px', marginBottom: '10px',
      }}>
        <h6 style={{ margin: 0, fontWeight: 700, marginRight: '4px' }}>
          {databaseType}{' '}
          {isRole
            ? (derived ? 'Roles' : 'Role Certification')
            : 'Privilege Certification'}
        </h6>
        {!(derived && isRole) && (
          <span style={{ fontSize: '0.8rem', color: '#555', marginRight: '6px' }}>
            {counts.certified}/{certifiableTotal} certified ({pct}%)
            {counts.derived > 0 && (
              <span className="text-muted"> · {counts.derived} derived</span>
            )}
          </span>
        )}
        {derived && isRole && (
          <span style={{ fontSize: '0.8rem', color: '#555', marginRight: '6px' }}>
            {counts.total} {counts.total === 1 ? 'role' : 'roles'} · derived from component privileges
          </span>
        )}
        <span style={{ fontSize: '0.78rem', color: '#6c757d', whiteSpace: 'nowrap' }}
          title="Click a card to filter the list">
          <i className="fas fa-filter me-1" />Filter (click a card):
        </span>
        {!(derived && isRole) && chip('unassigned', 'Unassigned', '#dc3545')}
        {!(derived && isRole) && chip('uncertified', 'Not certified', '#fd7e14')}
        {!(derived && isRole) && chip('certified', 'Certified', '#28a745')}
        {/* Derived (read-only) roles — built-in roles like `public` whose level
            is inherited, not certified here. Shown only when present so it makes
            the status row reconcile (unassigned+uncertified+certified+derived =
            All) and avoids the "27 vs 35" confusion vs the Role-kind row. */}
        {!(derived && isRole) && counts.derived > 0 && chip('derived', 'Derived', '#6f42c1')}
        {!(derived && isRole) && chip('all', 'All', '#6c757d')}
        {derived && isRole && (() => {
          // Severity-desc order. Show UNASSIGNED first when present (it's the
          // tier that needs DBA attention — a role is UNASSIGNED if any of its
          // component privileges is unassigned). Then enabled matrix tiers
          // that actually have roles in them; suppress zero-count tiers to
          // keep the row compact.
          const order = ['SUPER_ADMIN','ADMIN','WRITE_ELEVATED','WRITE','READ_ELEVATED','READ','NONE'];
          const enabled = new Set((privilegeLevels || []).map(l => l.name));
          const present = order.filter(n => enabled.has(n) && (levelCounts[n] || 0) > 0);
          const cards = [];
          if ((levelCounts['UNASSIGNED'] || 0) > 0) cards.push(levelChip('UNASSIGNED', 'Unassigned'));
          present.forEach(n => cards.push(levelChip(n, n)));
          return cards;
        })()}
        <div style={{ flex: 1, minWidth: '8px' }} />
        <input type="date" className="form-control form-control-sm"
          style={{ width: '140px', fontSize: '0.78rem' }}
          title="As-of date"
          value={asOfDate} max={new Date().toISOString().slice(0, 10)}
          onChange={e => setAsOfDate(e.target.value)} />
        <button className="btn btn-sm btn-outline-dark" style={{ fontSize: '0.78rem' }}
          disabled={busy || !asOfDate} onClick={openAsOf}
          title="View levels as of selected date">
          <i className="fas fa-calendar-day me-1" />As of
        </button>
        <button className="btn btn-sm btn-dark" style={{ fontSize: '0.78rem' }}
          onClick={openAudit}
          title="View audit report">
          <i className="fas fa-clipboard-list me-1" />Audit
        </button>
      </div>

      {/* Maintained (global) vs user-defined (per-db) role-kind filter cards.
          Oracle: predefined (global) vs user-defined (per-db) derived roles.
          MSSQL: fixed/built-in (global, certifiable) vs user-defined (per-db,
          read-only). Clickable cards matching the status-chip pattern above. */}
      {showKindFilter && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          gap: '8px', marginBottom: '10px',
        }}>
          <span style={{ fontSize: '0.78rem', color: '#6c757d', whiteSpace: 'nowrap' }}
            title="Filter by role kind">
            <i className="fas fa-layer-group me-1" />Role kind:
          </span>
          {kindChip('all', 'All roles', '#6c757d')}
          {kindChip('maintained', maintainedLabel, '#0d6efd')}
          {/* Built-in but read-only/per-db (MSSQL `public`). Only shown when
              such roles exist, so the kind row reconciles with the status row's
              Derived card and the Fixed/built-in count stays purely certifiable. */}
          {roleKindCounts.derived > 0 && kindChip('derived', 'Built-in derived', '#8b5cf6')}
          {kindChip('user', 'User-defined', '#6f42c1')}
        </div>
      )}

      {/* Sticky toolbar: filters + actions on one line. Filters narrow the
          list AND scope the bulk actions. */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5, background: '#eef6ff',
        border: '1px solid #b3d9ff', borderRadius: '8px', padding: '10px 12px',
        marginBottom: '12px',
        display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
      }}>
        <input
          className="form-control form-control-sm"
          style={{ width: '160px', fontSize: '0.82rem' }}
          placeholder={`Search ${noun}s…`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {/* Suggested-level filter — N/A for derived (Oracle) roles, which
            carry no suggestion. */}
        {!derived && (
          <select className="form-select form-select-sm" style={{ width: '150px', fontSize: '0.82rem' }}
            title="Filter by suggested level"
            value={fSuggested} onChange={e => { setFSuggested(e.target.value); setSelected(new Set()); }}>
            <option value="">All Suggestions</option>
            {privilegeLevels.map(l => <option key={l.value} value={l.name}>Suggested: {l.name}</option>)}
          </select>
        )}
        {/* Oracle role-kind filter is now rendered as cards above the
            toolbar (see kindChip), replacing the old dropdown.
            Current-level dropdown is suppressed for derived (Oracle) roles —
            the derived-level cards in the header already cover this filter
            (one click per tier, with live counts). Keep the dropdown for
            non-derived modes where the header chips are status-based, not
            level-based. */}
        {!(derived && isRole) && (
          <select className="form-select form-select-sm" style={{ width: '145px', fontSize: '0.82rem' }}
            title="Filter by current level"
            value={fCurrent} onChange={e => { setFCurrent(e.target.value); setSelected(new Set()); }}>
            <option value="">All Levels</option>
            <option value="UNASSIGNED">UNASSIGNED</option>
            {privilegeLevels.map(l => <option key={l.value} value={l.name}>{l.name}</option>)}
          </select>
        )}
        {!isRole && (
          <select className="form-select form-select-sm" style={{ width: '120px', fontSize: '0.82rem' }}
            title="Filter by type"
            value={fType} onChange={e => { setFType(e.target.value); setSelected(new Set()); }}>
            <option value="">All Types</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <button className="btn btn-sm btn-outline-secondary"
          style={{ fontSize: '0.82rem' }}
          disabled={!(search || fSuggested || fCurrent || fType)}
          onClick={() => { setSearch(''); setFSuggested(''); setFCurrent(''); setFType(''); setSelected(new Set()); }}
          title="Clear all filters">
          <i className="fas fa-times-circle" />
        </button>

        <span style={{ fontSize: '0.8rem', color: '#0c4a6e', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {selected.size > 0 ? `${selected.size} selected` : `${visible.length} shown`}
        </span>
        <div style={{ flex: 1, minWidth: '8px' }} />

        {/* Derived (Oracle) roles are read-only: level is computed from
            component privileges, never assigned/suggested/certified here.
            All forward/reverse mutation actions are hidden. */}
        {!derived && (
          <>
            {/* Forward actions */}
            <button className="btn btn-sm btn-info text-white" disabled={busy}
              onClick={() => openReview(false)}
              title="Apply suggested levels to selected">
              <i className="fas fa-lightbulb me-1" />Accept suggestions…
            </button>
            <button className="btn btn-sm btn-success" disabled={busy}
              onClick={() => openReview(true)}
              title="Apply suggested levels and certify selected">
              <i className="fas fa-bolt me-1" />Accept + Certify…
            </button>
            <button className="btn btn-sm btn-primary" disabled={busy}
              onClick={certifyAllAssigned}
              title="Certify selected">
              <i className="fas fa-certificate me-1" />Certify assigned
            </button>
            {/* Reverse actions (req 1) — these open a review modal first; they
                NEVER apply to everything on a single click. */}
            <button className="btn btn-sm btn-outline-warning" disabled={busy}
              onClick={() => openStateReview('uncertify')}
              title="Uncertify selected">
              <i className="fas fa-undo me-1" />Uncertify…
            </button>
            <button className="btn btn-sm btn-outline-danger" disabled={busy}
              onClick={() => openStateReview('unassign')}
              title="Revert selected to UNASSIGNED">
              <i className="fas fa-eraser me-1" />Unassign…
            </button>
          </>
        )}
        <button className="btn btn-sm btn-outline-secondary" disabled={busy}
          onClick={fetchRows} title="Refresh">
          <i className="fas fa-sync" />
        </button>
      </div>

      {toast && (
        <div className={`alert ${toast.ok ? 'alert-success' : 'alert-danger'} py-2`}
          style={{ fontSize: '0.85rem' }}>
          {toast.msg}
        </div>
      )}
      {/* §K63: persistent, prominent, dismissable cascade note. Stays until the
          DBA closes it (a compliance-relevant caveat, not a fleeting flash). */}
      {note && (
        <div className="alert alert-warning d-flex align-items-start"
          style={{ border: '1px solid #f0ad4e', borderLeft: '5px solid #f0ad4e',
                   fontSize: '0.9rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
          role="alert">
          <i className="fas fa-circle-info me-2 mt-1" style={{ color: '#b8860b' }} />
          <div className="cascade-note-text" style={{ flex: 1 }}>
            How this level change takes effect: {note}
          </div>
          <button type="button" className="btn-close ms-2"
            aria-label="Dismiss" onClick={() => setNote(null)} />
        </div>
      )}
      {error && (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          <i className="fas fa-spinner fa-spin fa-2x" />
          <div className="mt-2">Loading {noun}s…</div>
        </div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          <i className="fas fa-check-circle fa-2x text-success" />
          <div className="mt-2">Nothing in this view — great work.</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
          <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.85rem' }}>
            <thead className="table-dark">
              <tr>
                {!derived && (
                  <th style={{ width: '36px' }}>
                    <input type="checkbox"
                      checked={selectableVisible.length > 0 && selected.size === selectableVisible.length}
                      onChange={e => toggleSelAll(e.target.checked)} />
                  </th>
                )}
                <th style={{ maxWidth: isRole ? '280px' : '360px' }}>
                  {isRole ? 'Role' : 'Privilege'}
                </th>
                {showKindFilter && <th>Kind</th>}
                {!isRole && <th>Scope</th>}
                <th>{derived ? 'Derived Level' : 'Current Level'}</th>
                {!derived && <th>Suggested</th>}
                {!derived && <th>Certified</th>}
                {!derived && <th style={{ width: '230px' }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const st = rowState(r);
                const readOnly = isReadOnlyRow(r);
                return (
                  <tr key={rowKey(r)}>
                    {!derived && (
                      <td>
                        {/* User-defined (read-only) rows can't be acted on, so
                            no checkbox — they never enter the bulk scope. */}
                        {!readOnly && (
                          <input type="checkbox"
                            checked={selected.has(rowKey(r))}
                            onChange={() => toggleSel(rowKey(r))} />
                        )}
                      </td>
                    )}
                    <td style={{
                      fontWeight: 600, maxWidth: isRole ? '280px' : '360px',
                      whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }} title={rowDisplayName(r)}>
                      {rowDisplayName(r)}
                      <button className="btn btn-link btn-sm p-0 ms-2"
                        style={{ fontSize: '0.72rem' }}
                        title="View change history (who/when/why)"
                        onClick={() => openHistory(r)}>
                        <i className="fas fa-history" />
                      </button>
                    </td>
                    {showKindFilter && (
                      <td style={{ fontSize: '0.78rem' }}>
                        {/* Built-in/user-defined classification + the role's
                            scope (Server vs Database role). Oracle has no
                            server/database distinction, so the scope tag is
                            shown only when the row carries is_server_role. */}
                        {isMaintainedRole(r)
                          ? <span className="text-info" title={`${maintainedLabel} role`}>{maintainedLabel}</span>
                          : <span className="text-secondary" title="User-defined role — per database">User-defined</span>}
                        {typeof r.is_server_role === 'boolean' && (
                          <span className="text-muted ms-1" style={{ fontSize: '0.72rem' }}>
                            · {r.is_server_role ? 'Server role' : 'Database role'}
                          </span>
                        )}
                      </td>
                    )}
                    {!isRole && <td>{r.privilege_type}</td>}
                    {derived ? (
                      <td>
                        {/* Clickable: opens the component-privilege breakdown
                            that explains how this derived level was computed. */}
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          style={{ verticalAlign: 'baseline' }}
                          title="Show the component privileges behind this derived level"
                          onClick={() => openComponents(r)}
                        >
                          <LevelBadge name={r.privilege_level_name} />
                          <span className="text-muted ms-2" style={{ fontSize: '0.72rem' }}>
                            <i className="fas fa-calculator me-1" />derived
                            <i className="fas fa-up-right-from-square ms-1" style={{ fontSize: '0.65rem' }} />
                          </span>
                        </button>
                      </td>
                    ) : readOnly ? (
                      <td>
                        {/* User-defined / public MSSQL role: level is DERIVED
                            from its component privileges (read-only). Clickable
                            for MSSQL → opens the same component breakdown the
                            Oracle derived roles use, so a DBA can see which
                            grant drives the level. */}
                        {databaseType === 'MSSQL' ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none"
                            style={{ verticalAlign: 'baseline' }}
                            title="Show the component privileges behind this derived level"
                            onClick={() => openComponents(r)}
                          >
                            <LevelBadge name={r.privilege_level_name} />
                            <span className="text-muted ms-2" style={{ fontSize: '0.72rem' }}>
                              <i className="fas fa-calculator me-1" />derived
                              <i className="fas fa-up-right-from-square ms-1" style={{ fontSize: '0.65rem' }} />
                            </span>
                          </button>
                        ) : (
                          <>
                            <LevelBadge name={r.privilege_level_name} />
                            <span className="text-muted ms-2" style={{ fontSize: '0.72rem' }}
                              title="User-defined roles inherit their level from their component privileges and are not certified here">
                              <i className="fas fa-lock me-1" />inherited
                            </span>
                          </>
                        )}
                      </td>
                    ) : (
                    <td>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: '150px', display: 'inline-block' }}
                        value={st === 'unassigned' ? '' : r.privilege_level_name}
                        disabled={busy}
                        onChange={e => e.target.value && assignLevel(r, e.target.value)}
                      >
                        <option value="">
                          {st === 'unassigned' ? '— assign —' : r.privilege_level_name}
                        </option>
                        {privilegeLevels.map(l => (
                          <option key={l.value} value={l.name}>{l.name}</option>
                        ))}
                      </select>
                    </td>
                    )}
                    {!derived && (
                    <>
                    {readOnly ? (
                      <>
                        {/* Read-only role (user-defined, or built-in-derived
                            like `public`): no suggestion + no manual action.
                            Built-in-derived rows are AUTO-CERTIFIED, so the
                            Certified column reflects that; user-defined rows
                            that aren't certified show a muted dash. */}
                        <td><span className="text-muted">—</span></td>
                        <td style={{ fontSize: '0.78rem' }}>
                          {r.certified ? (
                            <span className="text-success" title="Auto-certified — built-in role, level derived from its grants">
                              <i className="fas fa-check-circle me-1" />
                              {r.certified_by || 'DERIVED'}
                              {r.certified_date && (
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                  {fmtDate(r.certified_date)}
                                </div>
                              )}
                            </span>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td><span className="text-muted" style={{ fontSize: '0.78rem' }}>
                          <i className="fas fa-lock me-1" />Read-only
                        </span></td>
                      </>
                    ) : (
                    <>
                    <td>
                      {r.suggested_level_name ? (
                        <span title="Documentation-derived suggestion (matrix-clamped). The DBA decides.">
                          <LevelBadge name={r.suggested_level_name} />
                          {/* "use" whenever the suggestion differs from the
                              current level — covers UNASSIGNED rows AND
                              assigned/certified rows whose level no longer
                              matches the (possibly matrix-shifted) suggestion. */}
                          {r.privilege_level_name !== r.suggested_level_name && (
                            <button
                              className="btn btn-link btn-sm p-0 ms-2"
                              style={{ fontSize: '0.75rem' }}
                              disabled={busy}
                              onClick={() => assignLevel(r, r.suggested_level_name)}
                              title={st === 'unassigned'
                                ? `Assign suggested level (${r.suggested_level_name})`
                                : `Change ${r.privilege_level_name} → ${r.suggested_level_name} (suggested)`}
                            >use</button>
                          )}
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td style={{ fontSize: '0.78rem' }}>
                      {st === 'certified' ? (
                        <span className="text-success">
                          <i className="fas fa-check-circle me-1" />
                          {r.certified_by || '—'}
                          {r.certified_date && (
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                              {fmtDate(r.certified_date)}
                            </div>
                          )}
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <div style={{
                        display: 'flex', gap: '4px', flexWrap: 'nowrap',
                        whiteSpace: 'nowrap', alignItems: 'center',
                      }}>
                        {st === 'unassigned' && (
                          <span className="text-danger" style={{ fontSize: '0.78rem' }}>
                            <i className="fas fa-tag me-1" />Assign a level first
                          </span>
                        )}
                        {st === 'uncertified' && (
                          <>
                            <button className="btn btn-sm btn-success" disabled={busy}
                              title="Certify this item"
                              onClick={() => certifyRow(r)}>
                              <i className="fas fa-check me-1" />Certify
                            </button>
                            <button className="btn btn-sm btn-outline-danger" disabled={busy}
                              title="Revert to UNASSIGNED"
                              onClick={() => openConfirm('unassign', r)}>
                              <i className="fas fa-eraser" />
                            </button>
                          </>
                        )}
                        {st === 'certified' && (
                          <>
                            <button className="btn btn-sm btn-outline-warning" disabled={busy}
                              title="Uncertify"
                              onClick={() => openConfirm('uncertify', r)}>
                              <i className="fas fa-undo" />
                            </button>
                            <button className="btn btn-sm btn-outline-danger" disabled={busy}
                              title="Revert to UNASSIGNED"
                              onClick={() => openConfirm('unassign', r)}>
                              <i className="fas fa-eraser" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    </>
                    )}
                    </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Review-before-apply modal for bulk Accept (suggestions / +certify) */}
      <WideModal
        show={!!reviewModal}
        onClose={() => !busy && setReviewModal(null)}
        title={reviewModal?.certify
          ? 'Review — Accept Suggestions & Certify'
          : 'Review — Accept Suggestions'}
        headerIcon="fa-lightbulb"
        headerClass="bg-info text-white"
        footer={
          <>
            <button className="btn btn-secondary" disabled={busy}
              onClick={() => setReviewModal(null)}>Cancel</button>
            <button className="btn btn-info text-white" disabled={busy
              || !reviewModal || reviewModal.included.size === 0}
              onClick={confirmReview}>
              {busy ? (
                <><i className="fas fa-spinner fa-spin me-2" />Applying…</>
              ) : (
                <><i className="fas fa-check me-2" />
                  Apply to {reviewModal?.included.size || 0} {noun}(s)
                  {reviewModal?.certify ? ' & Certify' : ''}</>
              )}
            </button>
          </>
        }
      >
        {reviewModal && (
          <>
            <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
              Each row will be set to suggested privilege level{reviewModal.certify ? ' and certified' : ''}.
              Uncheck any you don't want to include.
            </div>
            <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
              <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ width: '36px' }}>
                      <input type="checkbox"
                        checked={reviewModal.included.size === reviewModal.rows.length}
                        onChange={e => setReviewModal(m => ({
                          ...m,
                          included: e.target.checked
                            ? new Set(m.rows.map(rowKey)) : new Set(),
                        }))} />
                    </th>
                    <th>{isRole ? 'Role' : 'Privilege'}</th>
                    {!isRole && <th>Scope</th>}
                    <th>Current</th>
                    <th>Suggested →</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewModal.rows.map(r => (
                    <tr key={rowKey(r)}>
                      <td>
                        <input type="checkbox"
                          checked={reviewModal.included.has(rowKey(r))}
                          onChange={() => setReviewModal(m => {
                            const inc = new Set(m.included);
                            inc.has(rowKey(r)) ? inc.delete(rowKey(r)) : inc.add(rowKey(r));
                            return { ...m, included: inc };
                          })} />
                      </td>
                      <td style={{ fontWeight: 600 }}>{rowDisplayName(r)}</td>
                      {!isRole && <td>{r.privilege_type}</td>}
                      <td>
                        <span className="text-danger">
                          <i className="fas fa-tag me-1" />UNASSIGNED
                        </span>
                      </td>
                      <td><LevelBadge name={r.suggested_level_name} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </WideModal>

      {/* Oracle derived role: component privilege-level breakdown. Opened by
          clicking a role's Derived Level. Shows every component privilege with
          its own classified level, flags the driver(s) at the MAX, and any
          UNASSIGNED component that taints the role. */}
      <WideModal
        show={!!componentModal}
        onClose={() => setComponentModal(null)}
        title={`Derived Level — ${componentModal?.name || ''}`}
        headerIcon="fa-calculator"
        headerClass="bg-dark text-white"
        footer={
          <button className="btn btn-secondary"
            onClick={() => setComponentModal(null)}>Close</button>
        }
      >
        {componentModal && (
          componentModal.loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <i className="fas fa-spinner fa-spin fa-2x text-muted" />
            </div>
          ) : componentModal.error ? (
            <div className="alert alert-danger">{componentModal.error}</div>
          ) : componentModal.data ? (() => {
            const d = componentModal.data;
            const isUnassigned = d.derived_level_name === 'UNASSIGNED';
            // The stored per-(role,dbid) level can lag the live re-derivation
            // on rows the recalculator hasn't touched recently (e.g. an orphan
            // DB filtered out of the live view). Surface the mismatch rather
            // than silently showing two different levels.
            const stale = d.stored_level_name && d.stored_level_name !== d.derived_level_name;
            return (
              <>
                {stale && (
                  <div className="alert alert-warning py-2" style={{ fontSize: '0.82rem' }}>
                    <i className="fas fa-info-circle me-1" />
                    The stored level for this scope is <strong>{d.stored_level_name}</strong>,
                    but recomputing from the current component privileges gives
                    {' '}<strong>{d.derived_level_name}</strong>. Run
                    {' '}<strong>Recategorize Roles</strong> to refresh the stored value.
                  </div>
                )}
                {/* Summary strip: the derivation rule + the result. */}
                <div className="d-flex align-items-center flex-wrap gap-3 mb-3 p-3"
                  style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>Derived level</div>
                    <LevelBadge name={d.derived_level_name} />
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {isUnassigned ? (
                      <>
                        <i className="fas fa-exclamation-triangle text-warning me-1" />
                        UNASSIGNED because <strong>{d.counts.unassigned}</strong> component
                        {' '}privilege{d.counts.unassigned === 1 ? ' is' : 's are'} not yet classified.
                        A role's level can't be trusted until every component is assigned.
                      </>
                    ) : (
                      <>
                        Level = <strong>MAX</strong> of all component privilege levels.
                        {' '}The highlighted row{d.components.filter(c => c.is_driver).length === 1 ? '' : 's'} drive
                        {d.components.filter(c => c.is_driver).length === 1 ? 's' : ''} this result.
                      </>
                    )}
                  </div>
                  <div className="ms-auto text-muted" style={{ fontSize: '0.78rem' }}>
                    {d.is_oracle_maintained
                      ? <span className="text-info">Oracle-maintained (global)</span>
                      : d.is_mssql_maintained
                        ? <span className="text-info">Built-in · {d.database_name || 'server'}{typeof d.is_server_role === 'boolean' ? (d.is_server_role ? ' · server role' : ' · database role') : ''}</span>
                        : <span>User-defined{d.database_name ? ` · ${d.database_name}` : ''}{d.dbid ? ` · dbid ${d.dbid}` : ''}{typeof d.is_server_role === 'boolean' ? (d.is_server_role ? ' · server role' : ' · database role') : ''}</span>}
                  </div>
                </div>

                {d.components.length === 0 ? (
                  <div className="text-muted" style={{ padding: '10px' }}>
                    {d.is_superuser ? (
                      <>
                        <i className="fas fa-shield-alt text-info me-2" />
                        This role is a <strong>Cassandra superuser</strong>, so its
                        level is set to the matrix cap
                        {' '}(<LevelBadge name={d.matrix_cap_name} />)
                        {' '}regardless of explicit grants. There are no component
                        privileges to break down.
                      </>
                    ) : (
                      <>
                        This role has no direct component privileges
                        {d.granted_roles && d.granted_roles.length > 0
                          ? '; its level comes from granted sub-roles below.'
                          : '.'}
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                      <thead className="table-dark">
                        <tr>
                          <th>Component Privilege</th>
                          <th>Scope</th>
                          <th>Level</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Sort by level desc so the driver(s) sit at the top;
                            UNASSIGNED (-1) is shown first as it's the blocker. */}
                        {[...d.components]
                          .sort((a, b) => {
                            const av = a.unassigned ? 99 : a.level;
                            const bv = b.unassigned ? 99 : b.level;
                            return bv - av;
                          })
                          .map((c, i) => {
                            // §K62 (MSSQL) 3-state: BLOCKED = grant cancelled by a
                            // DENY (shown greyed/struck, not counted); DENY = the
                            // deny row itself. Defensive: only styles when the
                            // backend tagged effective_state (other techs omit it).
                            const st = c.effective_state;
                            const isBlocked = st === 'BLOCKED';
                            const isDeny = st === 'DENY';
                            const rowStyle = isBlocked
                              ? { background: '#f3f4f6', color: '#9ca3af' }
                              : isDeny
                                ? { background: '#fef2f2' }
                                : (c.is_driver ? { background: '#fff8e1' } : undefined);
                            return (
                            <tr key={i} style={rowStyle}>
                              <td style={{ fontWeight: 600, textDecoration: isBlocked ? 'line-through' : undefined }}>
                                {c.privilege}{c.object_name ? <span className="text-muted fw-normal"> · {c.object_name}</span> : null}
                              </td>
                              <td className="text-muted">{c.scope}</td>
                              <td><LevelBadge name={c.level_name} /></td>
                              <td style={{ fontSize: '0.75rem' }}>
                                {isDeny && (
                                  <span className="text-danger fw-semibold">
                                    <i className="fas fa-ban me-1" />DENY
                                  </span>
                                )}
                                {isBlocked && (
                                  <span className="text-muted fw-semibold">
                                    <i className="fas fa-ban me-1" />blocked by DENY (not counted)
                                  </span>
                                )}
                                {c.is_driver && (
                                  <span className="text-warning">
                                    <i className="fas fa-arrow-left me-1" />drives level
                                  </span>
                                )}
                                {c.unassigned && (
                                  <span className="text-danger">
                                    <i className="fas fa-tag me-1" />needs classification
                                  </span>
                                )}
                                {c.unknown && (
                                  <span className="text-muted" title="Not in the privilege reference; contributes NONE">
                                    <i className="fas fa-question-circle me-1" />unknown
                                  </span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Granted sub-roles, if any — each inherits its own derived level.
                    NOTE for Cassandra: inherited permissions are ALREADY flattened
                    into the components table above (rows with grant_type=INHERITED),
                    so this section is a chain summary, not a separate contribution
                    to the MAX. */}
                {d.granted_roles && d.granted_roles.length > 0 && (
                  <>
                    <div className="text-muted mt-3 mb-2" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      Granted sub-roles (inherited)
                      {databaseType === 'CASSANDRA' && (
                        <span className="ms-2 fw-normal" style={{ fontSize: '0.78rem' }}>
                          — these parents' permissions are already counted in the components above.
                        </span>
                      )}
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                      <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead className="table-dark">
                          <tr>
                            <th>Sub-role</th>
                            {d.granted_roles.some(g => typeof g.depth === 'number') && <th>Depth</th>}
                            <th>Level</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...d.granted_roles]
                            .sort((a, b) => {
                              // Drivers first, then by depth asc, then by name
                              if ((b.is_driver ? 1 : 0) !== (a.is_driver ? 1 : 0)) {
                                return (b.is_driver ? 1 : 0) - (a.is_driver ? 1 : 0);
                              }
                              const ad = typeof a.depth === 'number' ? a.depth : 99;
                              const bd = typeof b.depth === 'number' ? b.depth : 99;
                              if (ad !== bd) return ad - bd;
                              return (a.role || '').localeCompare(b.role || '');
                            })
                            .map((g, i) => (
                            <tr key={i} style={g.is_driver ? { background: '#fff8e1' } : undefined}>
                              <td style={{ fontWeight: 600 }}>{g.role}</td>
                              {d.granted_roles.some(gg => typeof gg.depth === 'number') && (
                                <td className="text-muted">{typeof g.depth === 'number' ? g.depth : '—'}</td>
                              )}
                              <td><LevelBadge name={g.level_name} /></td>
                              <td style={{ fontSize: '0.75rem' }}>
                                {g.is_driver && (
                                  <span className="text-warning">
                                    <i className="fas fa-arrow-left me-1" />drives level
                                  </span>
                                )}
                                {g.unassigned && (
                                  <span className="text-danger">
                                    <i className="fas fa-tag me-1" />needs classification
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            );
          })() : null
        )}
      </WideModal>

      {/* Per-row change-history timeline (req 4) */}
      <WideModal
        show={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={`Change History — ${historyModal?.name || ''}`}
        headerIcon="fa-history"
        headerClass="bg-dark text-white"
        footer={
          <button className="btn btn-secondary"
            onClick={() => setHistoryModal(null)}>Close</button>
        }
      >
        {historyModal && (
          historyModal.loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <i className="fas fa-spinner fa-spin fa-2x text-muted" />
            </div>
          ) : historyModal.error ? (
            <div className="alert alert-danger">{historyModal.error}</div>
          ) : historyModal.rows.length === 0 ? (
            <div className="text-muted" style={{ padding: '20px' }}>
              No recorded changes yet. (History is captured from the time the
              audit log was enabled; pipeline/seed activity is not back-filled.)
            </div>
          ) : (
            <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
              <thead className="table-dark">
                <tr>
                  <th>When</th><th>Action</th><th>Field</th>
                  <th>From → To</th><th>By</th><th>Source</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {historyModal.rows.map((h, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(h.changed_at)}</td>
                    <td><span className="badge bg-secondary">{h.action}</span></td>
                    <td>{h.field}</td>
                    <td>
                      <span className="text-muted">{h.old_value_name ?? '—'}</span>
                      {' → '}
                      <strong>{h.new_value_name ?? '—'}</strong>
                    </td>
                    <td>{h.changed_by}</td>
                    <td className="text-muted">{h.source || '—'}</td>
                    <td className="text-muted">{h.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </WideModal>

      {/* As-of-date snapshot (req 6) */}
      <WideModal
        show={!!asOfModal}
        onClose={() => setAsOfModal(null)}
        title={`${databaseType} ${isRole ? 'Roles' : 'Privileges'} — as of ${asOfModal?.date || ''}`}
        headerIcon="fa-calendar-day"
        headerClass="bg-dark text-white"
        footer={
          <button className="btn btn-secondary"
            onClick={() => setAsOfModal(null)}>Close</button>
        }
      >
        {asOfModal && (
          asOfModal.loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <i className="fas fa-spinner fa-spin fa-2x text-muted" />
            </div>
          ) : asOfModal.error ? (
            <div className="alert alert-danger">{asOfModal.error}</div>
          ) : (
            <>
              <div style={{ marginBottom: '10px', fontSize: '0.85rem', color: '#555' }}>
                Reconstructed from the audit log. <strong>As-of</strong> = state
                at end of {asOfModal.date}; <strong>Now</strong> = current state.
                A blank as-of means no recorded change by that date.
              </div>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                    <tr>
                      <th>{isRole ? 'Role' : 'Privilege'}</th>
                      <th>{isRole ? 'Kind' : 'Scope'}</th>
                      <th>Level (as of)</th>
                      <th>Certified (as of)</th>
                      <th>Level (now)</th>
                      <th>Certified (now)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asOfModal.items.map((it, i) => {
                      const drift = it.as_of_level_name &&
                        it.as_of_level_name !== it.current_level_name;
                      return (
                        <tr key={i} style={drift ? { background: '#fff8e1' } : undefined}>
                          <td style={{ fontWeight: 600 }}>{it.name}</td>
                          <td className={isRole ? 'text-muted' : undefined} style={isRole ? { fontSize: '0.75rem' } : undefined}>{it.scope || (isRole ? '' : '')}</td>
                          <td>{it.as_of_level_name
                            ? <LevelBadge name={it.as_of_level_name} />
                            : <span className="text-muted">—</span>}</td>
                          <td>{it.as_of_certified == null
                            ? <span className="text-muted">—</span>
                            : it.as_of_certified
                              ? <span className="text-success">Yes</span>
                              : <span className="text-danger">No</span>}</td>
                          <td><LevelBadge name={it.current_level_name} /></td>
                          <td>{it.current_certified
                            ? <span className="text-success">Yes</span>
                            : <span className="text-danger">No</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </WideModal>

      {/* Review-before-apply modal for bulk Unassign / Uncertify. The bulk
          reverse actions NEVER fire on one click — the DBA must confirm the
          exact rows here first. */}
      <WideModal
        show={!!stateModal}
        onClose={() => !busy && setStateModal(null)}
        title={stateModal?.action === 'unassign'
          ? 'Review — Unassign (back to UNASSIGNED)'
          : 'Review — Uncertify'}
        headerIcon={stateModal?.action === 'unassign' ? 'fa-eraser' : 'fa-undo'}
        headerClass={stateModal?.action === 'unassign'
          ? 'bg-danger text-white' : 'bg-warning'}
        footer={
          <>
            <button className="btn btn-secondary" disabled={busy}
              onClick={() => setStateModal(null)}>Cancel</button>
            <button
              className={`btn ${stateModal?.action === 'unassign'
                ? 'btn-danger' : 'btn-warning'}`}
              disabled={busy || !stateModal || stateModal.included.size === 0}
              onClick={confirmStateReview}>
              {busy ? (
                <><i className="fas fa-spinner fa-spin me-2" />Applying…</>
              ) : (
                <><i className={`fas ${stateModal?.action === 'unassign'
                  ? 'fa-eraser' : 'fa-undo'} me-2`} />
                  {stateModal?.action === 'unassign' ? 'Unassign' : 'Uncertify'}
                  {' '}{stateModal?.included.size || 0} {noun}(s)</>
              )}
            </button>
          </>
        }
      >
        {stateModal && (
          <>
            <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
              {stateModal.action === 'unassign'
                ? <>These rows will be reset to <strong>UNASSIGNED</strong>
                    {' '}(level cleared, and certification removed). Uncheck any
                    you want to keep.</>
                : <>These rows will be <strong>uncertified</strong> (the
                    assigned level is kept). Uncheck any you want to keep.</>}
            </div>
            <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
              <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ width: '36px' }}>
                      <input type="checkbox"
                        checked={stateModal.included.size === stateModal.rows.length}
                        onChange={e => setStateModal(m => ({
                          ...m,
                          included: e.target.checked
                            ? new Set(m.rows.map(rowKey)) : new Set(),
                        }))} />
                    </th>
                    <th>{isRole ? 'Role' : 'Privilege'}</th>
                    {!isRole && <th>Scope</th>}
                    <th>Current Level</th>
                    <th>Certified</th>
                  </tr>
                </thead>
                <tbody>
                  {stateModal.rows.map(r => (
                    <tr key={rowKey(r)}>
                      <td>
                        <input type="checkbox"
                          checked={stateModal.included.has(rowKey(r))}
                          onChange={() => setStateModal(m => {
                            const inc = new Set(m.included);
                            inc.has(rowKey(r)) ? inc.delete(rowKey(r)) : inc.add(rowKey(r));
                            return { ...m, included: inc };
                          })} />
                      </td>
                      <td style={{ fontWeight: 600 }}>{rowDisplayName(r)}</td>
                      {!isRole && <td>{r.privilege_type}</td>}
                      <td><LevelBadge name={r.privilege_level_name} /></td>
                      <td>{r.certified
                        ? <span className="text-success">Yes</span>
                        : <span className="text-muted">No</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </WideModal>

      {/* Single-row confirmation. A per-row Unassign / Uncertify used to fire
          immediately; this modal forces an explicit confirm with full detail
          on exactly what changes and the downstream effect. */}
      <WideModal
        show={!!confirmModal}
        onClose={() => !busy && setConfirmModal(null)}
        title={confirmModal?.action === 'unassign'
          ? `Confirm Unassign — ${noun}`
          : `Confirm Uncertify — ${noun}`}
        headerIcon={confirmModal?.action === 'unassign' ? 'fa-eraser' : 'fa-undo'}
        headerClass={confirmModal?.action === 'unassign'
          ? 'bg-danger text-white' : 'bg-warning'}
        footer={
          <>
            <button className="btn btn-secondary" disabled={busy}
              onClick={() => setConfirmModal(null)}>Cancel</button>
            <button
              className={`btn ${confirmModal?.action === 'unassign'
                ? 'btn-danger' : 'btn-warning'}`}
              disabled={busy}
              onClick={confirmSingle}>
              {busy ? (
                <><i className="fas fa-spinner fa-spin me-2" />Applying…</>
              ) : (
                <><i className={`fas ${confirmModal?.action === 'unassign'
                  ? 'fa-eraser' : 'fa-undo'} me-2`} />
                  {confirmModal?.action === 'unassign' ? 'Unassign' : 'Uncertify'}{' '}
                  this {noun}</>
              )}
            </button>
          </>
        }
      >
        {confirmModal && (() => {
          const r = confirmModal.row;
          const isUnassign = confirmModal.action === 'unassign';
          return (
            <>
              <div style={{
                background: isUnassign ? '#f8d7da' : '#fff3cd',
                border: `2px solid ${isUnassign ? '#dc3545' : '#ffc107'}`,
                borderRadius: '10px',
                padding: '14px 18px',
                marginBottom: '16px',
                color: isUnassign ? '#721c24' : '#856404',
                fontSize: '0.92rem',
                lineHeight: 1.5,
              }}>
                <i className="fas fa-exclamation-triangle me-2" />
                {isUnassign ? (
                  <>You are about to reset this {noun} to{' '}
                    <strong>UNASSIGNED</strong>. The assigned level is cleared{' '}
                    <strong>and</strong> its certification is removed. An
                    UNASSIGNED {noun} contributes <strong>nothing</strong> to a
                    user's computed privilege level until it is reassigned and
                    re-certified — so user access levels that depend on it may{' '}
                    <strong>drop</strong>.</>
                ) : (
                  <>You are about to <strong>uncertify</strong> this {noun}.
                    The assigned level is <strong>kept</strong>, but it is no
                    longer certified. Campaigns are blocked until every {noun}{' '}
                    is certified again.</>
                )}
              </div>

              <table className="table table-sm mb-3" style={{ fontSize: '0.9rem' }}>
                <tbody>
                  <tr>
                    <th style={{ width: '180px' }}>{isRole ? 'Role' : 'Privilege'}</th>
                    <td style={{ fontWeight: 600 }}>{rowDisplayName(r)}</td>
                  </tr>
                  {!isRole && (
                    <tr><th>Scope</th><td>{r.privilege_type || '—'}</td></tr>
                  )}
                  <tr>
                    <th>Current level</th>
                    <td><LevelBadge name={r.privilege_level_name} /></td>
                  </tr>
                  <tr>
                    <th>After this action</th>
                    <td>
                      {isUnassign
                        ? <LevelBadge name="UNASSIGNED" />
                        : <><LevelBadge name={r.privilege_level_name} />
                            {' '}<span className="text-muted">(level kept)</span></>}
                    </td>
                  </tr>
                  <tr>
                    <th>Certified</th>
                    <td>
                      {r.certified
                        ? <span className="text-success">
                            Yes{r.certified_by ? ` — by ${r.certified_by}` : ''}
                            {r.certified_date ? ` on ${fmtDate(r.certified_date)}` : ''}
                          </span>
                        : <span className="text-muted">No</span>}
                      {' → '}
                      {isUnassign || r.certified
                        ? <span className="text-danger">No (certification cleared)</span>
                        : <span className="text-muted">No</span>}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                <i className="fas fa-history me-1" />
                This change is recorded in the audit log (action, old → new
                value, your username, and timestamp) and triggers a
                recompute of affected user privilege levels.
              </div>
            </>
          );
        })()}
      </WideModal>

      {/* Full audit report — every recorded action for this tech/target. */}
      <WideModal
        show={!!auditModal}
        onClose={() => setAuditModal(null)}
        title={`${databaseType} ${isRole ? 'Roles' : 'Privileges'} — Audit Report`}
        headerIcon="fa-clipboard-list"
        headerClass="bg-dark text-white"
        footer={
          <button className="btn btn-secondary"
            onClick={() => setAuditModal(null)}>Close</button>
        }
      >
        {auditModal && (
          <>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '8px',
              alignItems: 'center', marginBottom: '10px',
            }}>
              <input className="form-control form-control-sm"
                style={{ width: '170px', fontSize: '0.82rem' }}
                placeholder={`Filter by ${noun} name…`}
                value={auditModal.filters.search}
                onChange={e => setAuditModal(m => ({ ...m,
                  filters: { ...m.filters, search: e.target.value } }))} />
              <select className="form-select form-select-sm"
                style={{ width: '180px', fontSize: '0.82rem' }}
                value={auditModal.filters.action}
                onChange={e => setAuditModal(m => ({ ...m,
                  filters: { ...m.filters, action: e.target.value } }))}>
                <option value="">All actions</option>
                <option value="assign">assign</option>
                <option value="unassign">unassign</option>
                <option value="certify">certify</option>
                <option value="uncertify">uncertify</option>
                <option value="bulk_apply_suggested">bulk_apply_suggested</option>
                {/* §K48 R-E: tier-disable remaps are audited since 2026-08-17 */}
                <option value="matrix_remap">matrix_remap</option>
              </select>
              <input className="form-control form-control-sm"
                style={{ width: '150px', fontSize: '0.82rem' }}
                placeholder="Changed by…"
                value={auditModal.filters.changedBy}
                onChange={e => setAuditModal(m => ({ ...m,
                  filters: { ...m.filters, changedBy: e.target.value } }))} />
              <input type="date" className="form-control form-control-sm"
                style={{ width: '150px', fontSize: '0.82rem' }}
                title="From date"
                value={auditModal.filters.since}
                onChange={e => setAuditModal(m => ({ ...m,
                  filters: { ...m.filters, since: e.target.value } }))} />
              <input type="date" className="form-control form-control-sm"
                style={{ width: '150px', fontSize: '0.82rem' }}
                title="To date"
                value={auditModal.filters.until}
                onChange={e => setAuditModal(m => ({ ...m,
                  filters: { ...m.filters, until: e.target.value } }))} />
              <button className="btn btn-sm btn-primary"
                style={{ fontSize: '0.82rem' }}
                disabled={auditModal.loading}
                onClick={() => loadAudit(auditModal.filters)}>
                <i className="fas fa-search me-1" />Apply
              </button>
              <span style={{ fontSize: '0.8rem', color: '#555' }}>
                {auditModal.loading ? 'Loading…'
                  : `${auditModal.entries.length} of ${auditModal.total} shown`}
              </span>
            </div>
            {auditModal.error ? (
              <div className="alert alert-danger">{auditModal.error}</div>
            ) : auditModal.loading ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <i className="fas fa-spinner fa-spin fa-2x text-muted" />
              </div>
            ) : auditModal.entries.length === 0 ? (
              <div className="text-muted" style={{ padding: '20px' }}>
                No audit entries match. (History is captured from when the
                audit log was enabled; pipeline/seed activity is not back-filled.)
              </div>
            ) : (
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.83rem' }}>
                  <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                    <tr>
                      <th>When</th><th>{isRole ? 'Role' : 'Privilege'}</th>
                      {/* §K49: same-named rows exist in several scopes — show it */}
                      <th>{isRole ? 'Kind' : 'Scope'}</th>
                      <th>Action</th><th>Field</th><th>From → To</th>
                      <th>By</th><th>Source</th><th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditModal.entries.map((h, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(h.changed_at)}</td>
                        <td style={{ fontWeight: 600 }}>{h.ref_name}</td>
                        <td className="text-muted" style={{ fontSize: '0.78rem' }}>{h.ref_scope || '—'}</td>
                        <td><span className="badge bg-secondary">{h.action}</span></td>
                        <td>{h.field}</td>
                        <td>
                          <span className="text-muted">{h.old_value_name ?? '—'}</span>
                          {' → '}
                          <strong>{h.new_value_name ?? '—'}</strong>
                        </td>
                        <td>{h.changed_by}</td>
                        <td className="text-muted">{h.source || '—'}</td>
                        <td className="text-muted">{h.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </WideModal>
    </div>
  );
}
